import { createContext, useContext } from "react";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cn } from "./cn";

/**
 * Pane that contained overlays portal into (the shell's content region), so
 * dialogs dim only the content they belong to and leave the chrome visible.
 * Provided by WorkspaceShell; without a provider dialogs fall back to
 * viewport-fixed. Opt out per-dialog with scope="global".
 */
export const OverlayContainerContext = createContext<React.RefObject<HTMLElement | null> | null>(null);

function useOverlayContainer(scope: "contained" | "global") {
    const ref = useContext(OverlayContainerContext);
    const contained = scope === "contained" && !!ref;
    // Base UI's Portal accepts a ref object; absolute overlays fill that pane.
    return { container: contained ? ref : undefined, pos: contained ? "absolute" : "fixed" };
}

/**
 * Modal dialog (Base UI). Compose as:
 *
 *   <Dialog.Root>
 *     <Dialog.Trigger render={<Button>Open</Button>} />
 *     <Dialog.Content>
 *       <Dialog.Title>Title</Dialog.Title>
 *       <Dialog.Description>Body…</Dialog.Description>
 *       <Dialog.Close render={<Button>Done</Button>} />
 *     </Dialog.Content>
 *   </Dialog.Root>
 */
export const Dialog = {
    Root: BaseDialog.Root,
    Trigger: BaseDialog.Trigger,
    Close: BaseDialog.Close,
    Title: function Title({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseDialog.Title>) {
        return (
            <BaseDialog.Title
                className={cn("text-lg font-semibold text-fg", className as string)}
                {...props}
            />
        );
    },
    Description: function Description({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseDialog.Description>) {
        return (
            <BaseDialog.Description
                className={cn("text-sm text-muted", className as string)}
                {...props}
            />
        );
    },
    /**
     * Centered card for form-style modals. Unlike Content it imposes no inner
     * layout (no flex/gap), so existing form markup (e.g. .form-grid) keeps its
     * own spacing. Width defaults to 460px to match the legacy .modal-card.
     */
    Panel: function Panel({
        className,
        children,
        scope = "contained",
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseDialog.Popup> & { scope?: "contained" | "global" }) {
        const { container, pos } = useOverlayContainer(scope);
        return (
            <BaseDialog.Portal container={container}>
                <BaseDialog.Backdrop
                    className={cn(
                        pos,
                        "inset-0 z-[50] bg-slate-900/30 backdrop-blur-[1px] transition-opacity duration-150",
                        "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
                    )}
                />
                <BaseDialog.Popup
                    className={cn(
                        pos,
                        "z-[51] overflow-auto border border-border bg-surface p-5 " +
                            "shadow-2xl outline-none transition-all " +
                            // <sm: iOS-style bottom sheet
                            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[85dvh] max-sm:rounded-t-[14px] " +
                            "max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] " +
                            "max-sm:duration-300 max-sm:ease-out " +
                            "max-sm:data-[starting-style]:translate-y-full max-sm:data-[ending-style]:translate-y-full " +
                            // ≥sm: centered card
                            "sm:left-1/2 sm:top-1/2 sm:w-[460px] sm:max-w-[calc(100vw-2rem)] " +
                            "sm:max-h-[90dvh] sm:-translate-x-1/2 sm:-translate-y-1/2 " +
                            "sm:rounded-[10px] sm:duration-150 " +
                            "sm:data-[starting-style]:scale-95 sm:data-[starting-style]:opacity-0 " +
                            "sm:data-[ending-style]:scale-95 sm:data-[ending-style]:opacity-0",
                        className as string,
                    )}
                    {...props}
                >
                    <div aria-hidden className="mx-auto mb-3 h-1 w-9 rounded-full bg-border sm:hidden" />
                    {children}
                </BaseDialog.Popup>
            </BaseDialog.Portal>
        );
    },
    /** Portal + backdrop + centered popup in one convenient wrapper. */
    Content: function Content({
        className,
        children,
        scope = "contained",
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseDialog.Popup> & { scope?: "contained" | "global" }) {
        const { container, pos } = useOverlayContainer(scope);
        return (
            <BaseDialog.Portal container={container}>
                <BaseDialog.Backdrop
                    className={cn(
                        pos,
                        "inset-0 z-[50] bg-slate-900/30 backdrop-blur-[1px] transition-opacity duration-150",
                        "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
                    )}
                />
                <BaseDialog.Popup
                    className={cn(
                        pos,
                        "z-[51] border border-border bg-surface p-5 shadow-xl outline-none " +
                            "flex flex-col gap-4 transition-all " +
                            // <sm: iOS-style bottom sheet
                            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[85dvh] max-sm:overflow-auto " +
                            "max-sm:rounded-t-[14px] max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0 " +
                            "max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] " +
                            "max-sm:duration-300 max-sm:ease-out " +
                            "max-sm:data-[starting-style]:translate-y-full max-sm:data-[ending-style]:translate-y-full " +
                            // ≥sm: centered card
                            "sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)] sm:max-w-md " +
                            "sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:duration-150 " +
                            "sm:data-[starting-style]:scale-95 sm:data-[starting-style]:opacity-0 " +
                            "sm:data-[ending-style]:scale-95 sm:data-[ending-style]:opacity-0",
                        className as string,
                    )}
                    {...props}
                >
                    <div aria-hidden className="mx-auto mb-1 h-1 w-9 shrink-0 rounded-full bg-border sm:hidden" />
                    {children}
                </BaseDialog.Popup>
            </BaseDialog.Portal>
        );
    },
};
